import React, {useEffect, useMemo, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {Redirect, useHistory, useRouteMatch} from 'react-router-dom';
import styled from 'styled-components';
import {Detail} from '@forecast-it/design-system';
import MissionControlHeader from '../type-script-files/MissionControlHeader';
import {CompanyFeatureFlagsModal} from '../company_feature_flags_modal';
import Util from '../../../forecast-app/shared/util/util';
import type {FeatureFlagsPage_viewer$key} from './__generated__/FeatureFlagsPage_viewer.graphql';
import type {MissionControlHeader_viewer$key} from '../type-script-files/__generated__/MissionControlHeader_viewer.graphql';
import {FeatureFlagListsScreen} from './lists/FeatureFlagListsScreen';
import {FeatureFlagRow} from './FeatureFlagRow';
import {
	FEATURE_FLAG_CREATED_COLUMN_INSET,
	FEATURE_FLAG_GRID_COLUMNS,
	FEATURE_FLAG_GRID_GAP,
	FEATURE_FLAG_GRID_PADDING_LEFT,
	FEATURE_FLAG_GRID_PADDING_RIGHT,
	FEATURE_FLAG_LIST_MIN_WIDTH,
} from './featureFlagGrid';
import {FEATURE_FLAG_PAGE_BACKGROUND} from './featureFlagSurface';
import {FeatureFlagUiSwitchLink} from './FeatureFlagUiSwitchLink';
import {FeatureFlagsToolbar} from './FeatureFlagsToolbar';
import {TeamLegend} from './TeamLegend';
import {FEATURE_FLAG_TEAMS} from './featureFlagTeams';
import {
	ALL_TEAMS,
	buildFeatureFlagRows,
	encodeCompanyId,
	filterFeatureFlagRows,
	getEligibleCompanyIds,
	resolveStoredTeamFilter,
	resolveTeamFilterChange,
	TeamFilterValue,
} from './featureFlagRows';
import {
	DEFAULT_FEATURE_FLAG_SORT,
	FeatureFlagSort,
	FeatureFlagSortColumn,
	resolveSortToggle,
	resolveStoredSort,
	serialiseSort,
	sortFeatureFlagRows,
} from './featureFlagSort';
import {buildCompanyFeatureFlags} from './companyFeatureFlags';
import {buildFeatureFlagDetail, findDetailFeature} from './featureFlagDetailData';
import {FeatureFlagDetail} from './FeatureFlagDetail';
import {withBetaProgramCohort} from './rollout/featureFlagRolloutCohort';
import {buildRolloutDirectory} from './rollout/featureFlagRolloutDirectory';
import {buildAlwaysLastCompanyIds, buildCohortViews} from './lists/featureFlagLists';

// Shared with the existing feature flags page so switching between the two keeps your filters.
// The team key's spelling is the one already in localStorage — changing it would silently orphan it.
const TEAM_FILTER_STORAGE_KEY = 'missin-control-featture-flag-team';
const SHOW_ROLLED_OUT_STORAGE_KEY = 'mission-control-feature-flag-showRolledOut';
const SORT_STORAGE_KEY = 'mission-control-feature-flag-sort';

const PageWrapper = styled.div`
	max-width: 1800px;
	/* Wide enough for the list rather than scrolling it inside itself, so a viewport too narrow for the
	   columns is scrolled by the document. Scrolling here would take the sticky column header below with
	   it: an auto overflow-x forces overflow-y to auto as well, which makes this element the header's
	   scrollport — and it never scrolls vertically, so the header would never move. */
	min-width: min-content;
	margin: 0 auto;
	padding: 28px 32px 64px;
`;

const ColumnHeader = styled.div`
	/* Held at the top of the window, so the labels stay with the rows they name — the list runs to
	   hundreds of flags, and a screen of scrolling otherwise leaves the operator reading unlabelled
	   columns. The document is the scroller (see PageWrapper), so this is the top of the window. Only the
	   vertical axis is pinned: the header still moves sideways with the rows, keeping the two aligned when
	   a narrow viewport is scrolled across. */
	position: sticky;
	top: 0;
	z-index: 1;
	/* Opaque, or the rows would show through the labels as they pass beneath. The page's ground rather
	   than the card surface: this strip is the gap between the cards, not a card of its own. */
	background: ${FEATURE_FLAG_PAGE_BACKGROUND};
	display: grid;
	min-width: ${FEATURE_FLAG_LIST_MIN_WIDTH};
	grid-template-columns: ${FEATURE_FLAG_GRID_COLUMNS};
	align-items: center;
	gap: ${FEATURE_FLAG_GRID_GAP};
	/* Padded at the top as well as the bottom, so the labels are not flush against the window's edge
	   once the header is pinned there. */
	padding: 8px ${FEATURE_FLAG_GRID_PADDING_RIGHT} 8px ${FEATURE_FLAG_GRID_PADDING_LEFT};
	/* Matches the row's border. Everything is border-box, so a row spends 2px of its width on its
	   own border and lays its columns out inside what is left; a header without the same border
	   would give its columns 2px more to share and sit misaligned against the rows beneath it. */
	border: 1px solid transparent;
`;

// Every header label sits in one of these, so they all share a box model. Wrapping only some of them
// leaves the rest aligned on their text baseline instead, which drops the wrapped ones a couple of
// pixels below their neighbours.
const HeaderCell = styled.span<{$alignRight?: boolean; $indentLeft?: string}>`
	display: flex;
	align-items: center;
	justify-content: ${props => (props.$alignRight ? 'flex-end' : 'flex-start')};
	min-width: 0;
	padding-left: ${props => props.$indentLeft ?? '0'};
`;

/**
 * A sortable label. A real button, so it is reachable by keyboard and announced as activatable; the
 * chrome is stripped back so it reads as a column label rather than a control. Inherits the
 * surrounding uppercase detail styling instead of restating it.
 */
const SortButton = styled.button<{$alignRight?: boolean}>`
	display: flex;
	align-items: center;
	justify-content: ${props => (props.$alignRight ? 'flex-end' : 'flex-start')};
	gap: 4px;
	width: 100%;
	min-width: 0;
	padding: 0;
	border: none;
	background: none;
	font: inherit;
	color: inherit;
	letter-spacing: inherit;
	text-transform: inherit;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

/**
 * Drawn only for the column currently sorted, so the list has exactly one arrow and it is obvious
 * which column it belongs to. Sized down and nudged off the text baseline: at label size the raw
 * glyph sits high and heavy next to the label.
 */
const SortChevron = styled.span`
	flex: none;
	font-size: 8px;
	line-height: 1;
	transform: translateY(1px);
`;

const RowList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: ${FEATURE_FLAG_LIST_MIN_WIDTH};
`;

export const featureFlagsPageQuery = graphql`
	query FeatureFlagsPage_Query {
		viewer {
			actualPersonId
			component(name: "control_feature_flags")
			...FeatureFlagsPage_viewer
		}
	}
`;

const featureFlagsPageFragment = graphql`
	fragment FeatureFlagsPage_viewer on Viewer {
		component(name: "control_feature_flags")
		id
		...MissionControlHeader_viewer
		missionControlData {
			id
			companies {
				id
				companyId
				name
				tier
				accessBlocked
				betaOptIn
			}
			forecastPersons {
				id
				firstName
				lastName
				email
			}
			otherPersons {
				id
				firstName
				lastName
				email
				companyId
			}
			availableFeatures {
				id
				name
				key
				team
				rolledOut
				rolledOutDate
				cleanedUp
				createdAt
				enabledForAllCompanies
				enabledForAllCompaniesAt
				enabledForAllCompaniesBy
				companies {
					id
					companyId
					name
					timestamp
				}
				persons {
					id
					personId
					companyId
					companyName
					name
					email
					timestamp
				}
				personsOptedOut {
					id
					companyId
					name
				}
			}
			featureFlagLists {
				id
				cohorts {
					id
					cohortId
					name
					companyIds
				}
				alwaysLastCompanyIds
			}
		}
	}
`;

interface FeatureFlagsPageProps {
	viewer: FeatureFlagsPage_viewer$key;
}

export default function FeatureFlagsPage({viewer}: FeatureFlagsPageProps): JSX.Element | null {
	const data = useFragment(featureFlagsPageFragment, viewer);

	// useRouteMatch with no argument resolves to the route this page is registered under in the
	// router — the flag list path. The route is non-exact, so a trailing flag key already matches it
	// and the detail screen needs no route of its own.
	const {path, url} = useRouteMatch();
	const history = useHistory();
	// Matched before the flag-key route below, which is non-exact and would otherwise swallow this
	// path as a flag named "lists" and redirect back to the list. That reserves "lists" as a flag key;
	// safe, because flag keys are code constants rather than anything an operator can type.
	const listsMatch = useRouteMatch(`${path}/lists`);
	const detailMatch = useRouteMatch<{flagKey: string}>(`${path}/:flagKey`);
	const detailFlagKey = listsMatch != null ? null : detailMatch?.params.flagKey ?? null;

	/*
	 * Which of this page's three screens is showing. Only used to reset the scroll position below, so a
	 * detail-to-detail move counts as a change while a filter change does not.
	 */
	const screenKey = listsMatch != null ? 'lists' : detailFlagKey ?? 'list';

	/*
	 * Move to the top of whichever screen was just opened.
	 *
	 * All three screens render from this one component, so a route change swaps the content underneath a
	 * tree that stays mounted and nothing moves the window — clicking a flag at the bottom of a long list
	 * would land the operator part-way down the detail screen they just opened. A real document
	 * navigation gets this from the browser; inside a single-page route change it has to be done here.
	 *
	 * A POP is deliberately left alone. Back out of a flag should return the operator to where they were
	 * in the list, which is what the browser already restores for a history entry it has a position for —
	 * resetting here as well would replace one misplaced scroll with another. The initial render is a POP
	 * too, so a freshly loaded or deep-linked URL is left to the browser as well.
	 */
	useEffect(() => {
		if (history.action === 'POP') {
			return;
		}
		window.scrollTo(0, 0);
	}, [screenKey]);

	const [teamFilter, setTeamFilter] = useState<TeamFilterValue>(() =>
		resolveStoredTeamFilter(Util.localStorageGetItemWithDefault(TEAM_FILTER_STORAGE_KEY, ALL_TEAMS))
	);
	const [showRolledOut, setShowRolledOut] = useState<boolean>(
		() => Util.localStorageGetItemWithDefault(SHOW_ROLLED_OUT_STORAGE_KEY, 'true') === 'true'
	);
	const [sort, setSort] = useState<FeatureFlagSort>(() =>
		resolveStoredSort(Util.localStorageGetItemWithDefault(SORT_STORAGE_KEY, serialiseSort(DEFAULT_FEATURE_FLAG_SORT)))
	);
	const [checkedCompanyId, setCheckedCompanyId] = useState<string | null>(null);
	// Bumped when the company modal closes, to clear the picker that opened it — see the toolbar's
	// companyPickerResetToken.
	const [companyPickerResetToken, setCompanyPickerResetToken] = useState(0);

	const missionControlData = data?.missionControlData;
	const availableFeatures = missionControlData?.availableFeatures;

	const eligibleCompanyIds = useMemo(() => getEligibleCompanyIds(missionControlData?.companies), [missionControlData]);
	const allRows = useMemo(
		() => buildFeatureFlagRows(availableFeatures, eligibleCompanyIds),
		[availableFeatures, eligibleCompanyIds]
	);
	const visibleRows = useMemo(
		() => sortFeatureFlagRows(filterFeatureFlagRows(allRows, {teamId: teamFilter, showRolledOut}), sort),
		[allRows, teamFilter, showRolledOut, sort]
	);

	const companyOptions = useMemo(
		() =>
			(missionControlData?.companies ?? [])
				.filter(company => company?.companyId != null)
				.map(company => ({
					id: String(company?.companyId),
					name: company?.name ?? '',
				})),
		[missionControlData]
	);

	// Always the same teams, in the same order as the legend — a fixed list, not one derived from
	// what happens to be loaded.
	const teamFilterOptions = useMemo(
		() => [
			{id: ALL_TEAMS as TeamFilterValue, name: 'All teams'},
			...FEATURE_FLAG_TEAMS.map(team => ({
				id: team.id as TeamFilterValue,
				name: team.label,
			})),
		],
		[]
	);

	const checkedCompanyFeatures = useMemo(
		() => (checkedCompanyId == null ? [] : buildCompanyFeatureFlags(availableFeatures, encodeCompanyId(checkedCompanyId))),
		[availableFeatures, checkedCompanyId]
	);

	// Encoded company ids for the manage-lists beta program tab, read from the same `betaOptIn` join
	// every eligible company already carries — there is no separate beta-program query.
	const betaCompanyIds = useMemo(
		() =>
			(missionControlData?.companies ?? [])
				.filter(company => company?.betaOptIn && company?.companyId != null)
				.map(company => encodeCompanyId(String(company?.companyId))),
		[missionControlData]
	);

	// Built once for the page and handed to the detail screen. The two person lists together are the
	// active, non-virtual, non-client population, which is what the rollout modal counts a company's
	// users by and picks individual users from.
	const rolloutDirectory = useMemo(
		() =>
			buildRolloutDirectory(
				missionControlData?.companies,
				missionControlData?.otherPersons,
				missionControlData?.forecastPersons
			),
		[missionControlData]
	);

	// The cohorts a rollout can target: the beta program, then the editable ones sorted by name. Shared
	// with the manage-lists screen's own view of them only in the sense that both derive from the same
	// query fields — the two screens are never mounted together, so each builds what it needs. The beta
	// program is prepended here rather than inside the modal so that the modal, like every other consumer
	// of a cohort, sees one list and does not have to know the beta program is a different kind of thing.
	const cohorts = useMemo(
		() => withBetaProgramCohort(buildCohortViews(missionControlData?.featureFlagLists?.cohorts), betaCompanyIds),
		[betaCompanyIds, missionControlData]
	);

	// A Set because the percentage fill tests membership per company; the encoding itself is shared with
	// the manage-lists screen so the two cannot disagree about what is on the list.
	const alwaysLastCompanyIds = useMemo(
		() => new Set(buildAlwaysLastCompanyIds(missionControlData?.featureFlagLists?.alwaysLastCompanyIds)),
		[missionControlData]
	);

	const detail = useMemo(() => {
		if (detailFlagKey == null) {
			return null;
		}
		const feature = findDetailFeature(availableFeatures, detailFlagKey);
		return feature == null ? null : buildFeatureFlagDetail(feature, eligibleCompanyIds);
	}, [availableFeatures, detailFlagKey, eligibleCompanyIds]);

	if (!missionControlData) {
		return null;
	}

	// A key no flag has, or a rolled-out flag, has no detail screen to show — go back to the list
	// rather than dead-ending on a bookmarked or hand-typed URL.
	if (detailFlagKey != null && detail == null) {
		return <Redirect to={url} />;
	}

	const handleTeamFilterChange = (value: TeamFilterValue | null): void => {
		const next = resolveTeamFilterChange(value);
		Util.localStorageSetItem(TEAM_FILTER_STORAGE_KEY, next);
		setTeamFilter(next);
	};

	const handleShowRolledOutChange = (value: boolean): void => {
		Util.localStorageSetItem(SHOW_ROLLED_OUT_STORAGE_KEY, String(value));
		setShowRolledOut(value);
	};

	const handleSort = (column: FeatureFlagSortColumn): void => {
		const next = resolveSortToggle(sort, column);
		Util.localStorageSetItem(SORT_STORAGE_KEY, serialiseSort(next));
		setSort(next);
	};

	const handleManageLists = (): void => {
		history.push(`${url}/lists`);
	};

	// Assigning to the header's fragment key is the check that keeps `...MissionControlHeader_viewer`
	// in the fragment above: drop the spread and this stops compiling. The cast below cannot do that
	// job — MissionControlHeader's props are typed as its resolved fragment data rather than a key, so
	// handing the container a key needs a cast, and a cast would silently accept anything.
	/**
	 * One column label. `aria-sort` goes on the cell rather than the button because it belongs to the
	 * column, and it is set on every sortable column — announcing "none" is what tells a screen reader
	 * the others are sortable but unsorted, rather than not sortable at all.
	 */
	const sortableHeader = (
		column: FeatureFlagSortColumn,
		label: string,
		options: {alignRight?: boolean; indentLeft?: string} = {}
	): JSX.Element => {
		const active = sort.column === column;
		const ariaSort = active ? (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none';
		return (
			<HeaderCell key={column} $alignRight={options.alignRight} $indentLeft={options.indentLeft} aria-sort={ariaSort}>
				<SortButton
					type="button"
					$alignRight={options.alignRight}
					data-cy={`feature-flag-sort-${column.toLowerCase()}`}
					onClick={() => handleSort(column)}
				>
					<Detail>{label}</Detail>
					{active ? <SortChevron aria-hidden="true">{sort.direction === 'ASC' ? '▲' : '▼'}</SortChevron> : null}
				</SortButton>
			</HeaderCell>
		);
	};

	const headerViewer: MissionControlHeader_viewer$key = data;

	return (
		<div>
			<MissionControlHeader
				viewer={headerViewer as unknown as React.ComponentProps<typeof MissionControlHeader>['viewer']}
			/>
			{listsMatch != null ? (
				<FeatureFlagListsScreen
					backPath={url}
					lists={missionControlData.featureFlagLists}
					directory={rolloutDirectory}
					betaCompanyIds={betaCompanyIds}
				/>
			) : detail != null ? (
				<FeatureFlagDetail
					alwaysLastCompanyIds={alwaysLastCompanyIds}
					backPath={url}
					cohorts={cohorts}
					detail={detail}
					directory={rolloutDirectory}
					eligibleCompanyIds={eligibleCompanyIds}
				/>
			) : (
				<PageWrapper>
					{/* Only on the list. The detail and manage-lists screens have a back link of their own, and a
					    second one beside it pointing somewhere else entirely would read as the same control. */}
					<FeatureFlagUiSwitchLink target="old" />
					<FeatureFlagsToolbar
						visibleCount={visibleRows.length}
						totalCount={allRows.length}
						teamFilter={teamFilter}
						onTeamFilterChange={handleTeamFilterChange}
						showRolledOut={showRolledOut}
						onShowRolledOutChange={handleShowRolledOutChange}
						companies={companyOptions}
						onCompanySelected={setCheckedCompanyId}
						teamFilterOptions={teamFilterOptions}
						companyPickerResetToken={companyPickerResetToken}
						onManageLists={handleManageLists}
					/>
					<TeamLegend />
					<ColumnHeader data-cy="feature-flag-column-header">
						{sortableHeader('NAME', 'FLAG')}
						{sortableHeader('TEAM', 'TEAM')}
						{sortableHeader('COMPANIES', 'COMPANIES', {alignRight: true})}
						{sortableHeader('USERS', 'USERS', {alignRight: true})}
						{sortableHeader('CREATED', 'CREATED', {indentLeft: FEATURE_FLAG_CREATED_COLUMN_INSET})}
						{sortableHeader('ROLLOUT', 'ROLLOUT')}
						<HeaderCell />
					</ColumnHeader>
					<RowList data-cy="feature-flag-list">
						{visibleRows.length === 0 ? (
							<Detail>No feature flags match the current filters.</Detail>
						) : (
							visibleRows.map(row => <FeatureFlagRow key={row.key} row={row} detailPath={`${url}/${row.key}`} />)
						)}
					</RowList>
				</PageWrapper>
			)}
			{checkedCompanyId != null && (
				<CompanyFeatureFlagsModal
					features={checkedCompanyFeatures}
					closeModal={() => {
						setCheckedCompanyId(null);
						setCompanyPickerResetToken(token => token + 1);
					}}
				/>
			)}
		</div>
	);
}
